// exportsCountries.ts

import React, { ReactNode } from "react"
import AusNZ from "../common/Icons/Countries/AUS-NZ"
import GCC from "../common/Icons/Countries/GCC"
import IND from "../common/Icons/Countries/IND"
import MULTI from "../common/Icons/Countries/MULTI"
import UK from "../common/Icons/Countries/UK"
import USA from "../common/Icons/Countries/USA"
import Canada from "../common/Icons/Countries/CANADA"
import BT from "../common/Icons/Countries/BT"
import PH from "../common/Icons/Countries/PH"
import HK from "../common/Icons/Countries/HK"
import SG from "../common/Icons/Countries/SG"
import MY from "../common/Icons/Countries/MY"

import {
  DOMESTIC_NUTRITION_KEYS,
  DOMESTIC_SCHEMA_KEYS,
} from "../Utils/constants"
import { getPhilippinesNiTableCalculatedValue } from "../components/LabelPreview/philippinesLabelPreview/PhilippinesNutritionTableUtils"
import { getMalaysiaNiTableCalculatedValue } from "../components/LabelPreview/MalaysiaLabelPreview/MalaysiaNutritionTableUtils"
import { getSingaporeNiTableCalculatedValue } from "../components/LabelPreview/SingaporeLabelPreview/SingaporeNutritionTableUtils"
import { getHongkongNiTableCalculatedValue } from "../components/LabelPreview/HongkongLabelPreview/HongkongNutritionTableUtils"
import { calculateIndiaNutritionTableValues } from "../components/LabelPreview/IndiaLabelPreview/NutritionIndiaHelper"
import { calculateUSANutritionTableValues } from "../components/LabelPreview/USALabelPreview/NutritionUSAhelper"
import { calculateCanadaNutritionTableValues } from "../components/LabelPreview/CanadaLabelPreview/NutritionCanadaHelper"
import { calculateGCCNutritionTableValues } from "../components/LabelPreview/GCCLabelPreview/NutritionGCCHelper"

/**
 * Interface for each export country item in the array.
 */
interface ExportCountryItem {
  name: string
  symbol: string
  flag: ReactNode // The rendered country flag icon
}

export const COUNTRY_CODE = {
  INDIA: "IND",
  USA: "USA",
  "AUS-NZ": "AUS-NZ",
  UK: "UK",
  GCC: "GCC",
  CANADA: "CA",
  BHUTAN: "BT",
  PHILIPPINES: "PH",
  MALAYSIA: "MY",
  SINGAPORE: "SG",
  HONG_KONG: "HK",
} as const

export const export_5_countries = [
  COUNTRY_CODE.BHUTAN,
  COUNTRY_CODE.PHILIPPINES,
  COUNTRY_CODE.MALAYSIA,
  COUNTRY_CODE.HONG_KONG,
  COUNTRY_CODE.SINGAPORE,
]

export const export_all_countries = [
  COUNTRY_CODE.BHUTAN,
  COUNTRY_CODE.PHILIPPINES,
  COUNTRY_CODE.MALAYSIA,
  COUNTRY_CODE.HONG_KONG,
  COUNTRY_CODE.SINGAPORE,
  COUNTRY_CODE.CANADA,
  COUNTRY_CODE.GCC,
  COUNTRY_CODE.USA,
  COUNTRY_CODE.UK,
  COUNTRY_CODE["AUS-NZ"],
]

export const exportsCountries: ExportCountryItem[] = [
  {
    name: "India",
    symbol: COUNTRY_CODE.INDIA,
    flag: <IND />,
  },
  {
    name: "Australia & New Zealand",
    symbol: COUNTRY_CODE["AUS-NZ"],
    flag: <AusNZ />,
  },
  {
    name: "Bhutan",
    symbol: COUNTRY_CODE.BHUTAN,
    flag: <BT />,
  },
  { name: "Canada", symbol: "CA", flag: <Canada /> },
  {
    name: "Gulf Cooperation Council",
    symbol: COUNTRY_CODE.GCC,
    flag: <GCC />,
  },
  {
    name: "Hong Kong",
    symbol: COUNTRY_CODE.HONG_KONG,
    flag: <HK />,
  },
  {
    name: "Malaysia",
    symbol: COUNTRY_CODE.MALAYSIA,
    flag: <MY />,
  },
  {
    name: "Philippines",
    symbol: COUNTRY_CODE.PHILIPPINES,
    flag: <PH />,
  },
  {
    name: "Singapore",
    symbol: COUNTRY_CODE.SINGAPORE,
    flag: <SG />,
  },
  {
    name: "United Kingdom",
    symbol: COUNTRY_CODE.UK,
    flag: <UK />,
  },
  {
    name: "United States of America",
    symbol: COUNTRY_CODE.USA,
    flag: <USA />,
  },
  {
    name: "Multi Country",
    symbol: "MULTI",
    flag: <MULTI />,
  },
]

/**
 * Export schema keys, referencing your DOMESTIC_SCHEMA_KEYS constants.
 * Type definitions can be expanded if you want stricter typing.
 */
export const EXPORT_SCHEMA_KEYS = {
  select_country: DOMESTIC_SCHEMA_KEYS.select_country,
  brandId: DOMESTIC_SCHEMA_KEYS.brandId,
  "Add Brand": DOMESTIC_SCHEMA_KEYS["Add Brand"],
  food_name: DOMESTIC_SCHEMA_KEYS.food_name,
  flavour: DOMESTIC_SCHEMA_KEYS.flavour,
  other_information: DOMESTIC_SCHEMA_KEYS.other_information,
  proprietary_product: DOMESTIC_SCHEMA_KEYS.proprietary_product,
  proprietary_food_name: DOMESTIC_SCHEMA_KEYS.proprietary_food_name,
  show_fssai_category: DOMESTIC_SCHEMA_KEYS.show_fssai_category,
  fssai_category_name: DOMESTIC_SCHEMA_KEYS.fssai_category_name,
  single_ingredients: DOMESTIC_SCHEMA_KEYS.single_ingredients,
  ingredients: DOMESTIC_SCHEMA_KEYS.ingredients,
  ingredient_name: DOMESTIC_SCHEMA_KEYS.ingredient_name,
  name: DOMESTIC_SCHEMA_KEYS.name,
  proportion: DOMESTIC_SCHEMA_KEYS.proportion,
  compound: DOMESTIC_SCHEMA_KEYS.compound,
  show_ingredient_proportion: DOMESTIC_SCHEMA_KEYS.show_ingredient_proportion,
  ingredient: DOMESTIC_SCHEMA_KEYS.ingredient,
  ingredient_message: DOMESTIC_SCHEMA_KEYS.ingredient_message,
  additives: DOMESTIC_SCHEMA_KEYS.additives,
  no_additives: DOMESTIC_SCHEMA_KEYS.no_additives,
  finalingredients: DOMESTIC_SCHEMA_KEYS.finalingredients,
  vegetarian_and_non_vegetarian:
    DOMESTIC_SCHEMA_KEYS.vegetarian_and_non_vegetarian,
  vegan_certificate: DOMESTIC_SCHEMA_KEYS.vegan_certificate,
  organic_product: DOMESTIC_SCHEMA_KEYS.organic_product,
  organic_certificate: DOMESTIC_SCHEMA_KEYS.organic_certificate,
  organic_certificate_from: DOMESTIC_SCHEMA_KEYS.organic_certificate_from,
  agmark_certified: DOMESTIC_SCHEMA_KEYS.agmark_certified,
  agmark_certificate: DOMESTIC_SCHEMA_KEYS.agmark_certificate,
  tea_board_certified: DOMESTIC_SCHEMA_KEYS.tea_board_certified,
  tea_board_certificate: DOMESTIC_SCHEMA_KEYS.tea_board_certificate,
  tea_india_certified: DOMESTIC_SCHEMA_KEYS.tea_india_certified,
  tea_india_certificate: DOMESTIC_SCHEMA_KEYS.tea_india_certificate,
  tea_india_license_number: DOMESTIC_SCHEMA_KEYS.tea_india_license_number,
  bis_certified: DOMESTIC_SCHEMA_KEYS.bis_certified,
  bis_certificate: DOMESTIC_SCHEMA_KEYS.bis_certificate,
  bis_license_number: DOMESTIC_SCHEMA_KEYS.bis_license_number,
  fortified: DOMESTIC_SCHEMA_KEYS.fortified,
  fortification_certificate: DOMESTIC_SCHEMA_KEYS.fortification_certificate,
  fortified_with: DOMESTIC_SCHEMA_KEYS.fortified_with,
  irradiated: DOMESTIC_SCHEMA_KEYS.irradiated,
  product_name: DOMESTIC_SCHEMA_KEYS.product_name,
  purpose: DOMESTIC_SCHEMA_KEYS.purpose,
  operating_licence_no: DOMESTIC_SCHEMA_KEYS.operating_licence_no,
  natural_product: DOMESTIC_SCHEMA_KEYS.natural_product,
  plant_based_product: DOMESTIC_SCHEMA_KEYS.plant_based_product,
  allergens: DOMESTIC_SCHEMA_KEYS.allergens,
  ingredientToDisplay: "ingredientToDisplay",
  contains: DOMESTIC_SCHEMA_KEYS.contains,
  may_contains: DOMESTIC_SCHEMA_KEYS.may_contains,
  my_product_is: DOMESTIC_SCHEMA_KEYS.my_product_is,
  ni_info_type: DOMESTIC_SCHEMA_KEYS.ni_info_type,
  lab_report: DOMESTIC_SCHEMA_KEYS.lab_report,
  your_own_values: DOMESTIC_SCHEMA_KEYS.your_own_values,
  PFL_RECIPE_SELECTION: DOMESTIC_SCHEMA_KEYS.PFL_RECIPE_SELECTION,
  RECIPE_SEARCH: DOMESTIC_SCHEMA_KEYS.RECIPE_SEARCH,
  target_audience: DOMESTIC_SCHEMA_KEYS.target_audience,
  age_group: DOMESTIC_SCHEMA_KEYS.age_group,
  serving_size: DOMESTIC_SCHEMA_KEYS.serving_size,
  serving_size_unit: DOMESTIC_SCHEMA_KEYS.serving_size_unit,
  net_quantity: DOMESTIC_SCHEMA_KEYS.net_quantity,
  net_quantity_unit: DOMESTIC_SCHEMA_KEYS.net_quantity_unit,
  mandatory_nutrient: DOMESTIC_SCHEMA_KEYS.mandatory_nutrient,
  non_mandatory_nutrient: DOMESTIC_SCHEMA_KEYS.non_mandatory_nutrient,
  industrial_trans_fat_free: DOMESTIC_SCHEMA_KEYS.industrial_trans_fat_free,
  manufacturer_by: DOMESTIC_SCHEMA_KEYS.manufacturer_by,
  packed_by: DOMESTIC_SCHEMA_KEYS.packed_by,
  marketed_by: DOMESTIC_SCHEMA_KEYS.marketed_by,
  brand_owner: DOMESTIC_SCHEMA_KEYS.brand_owner,
  imported: DOMESTIC_SCHEMA_KEYS.imported,
  country_of_origin: DOMESTIC_SCHEMA_KEYS.country_of_origin,
  imported_by: DOMESTIC_SCHEMA_KEYS.imported_by,
  "Create new address": DOMESTIC_SCHEMA_KEYS["Create new address"],
  consumer_care: DOMESTIC_SCHEMA_KEYS.consumer_care,
  use_storage_instructions: DOMESTIC_SCHEMA_KEYS.use_storage_instructions,
  made_in_india: DOMESTIC_SCHEMA_KEYS.made_in_india,
  plant_stanol_esters: DOMESTIC_SCHEMA_KEYS.plant_stanol_esters,
  plant_stanol_esters_qty: DOMESTIC_SCHEMA_KEYS.plant_stanol_esters_qty,
  isomaltulose: DOMESTIC_SCHEMA_KEYS.isomaltulose,
  isomaltulose_qty: DOMESTIC_SCHEMA_KEYS.isomaltulose_qty,
  annatto_color: DOMESTIC_SCHEMA_KEYS.annatto_color,
  "10%_or_more_polyols": DOMESTIC_SCHEMA_KEYS["10%_or_more_polyols"],
  "10%_or_more_polydextrose": DOMESTIC_SCHEMA_KEYS["10%_or_more_polydextrose"],
  added_caffeine: DOMESTIC_SCHEMA_KEYS.added_caffeine,
  "10%_or_more_sorbitol_and_sorbitol_syrup":
    DOMESTIC_SCHEMA_KEYS["10%_or_more_sorbitol_and_sorbitol_syrup"],
  trehalose: DOMESTIC_SCHEMA_KEYS.trehalose,
  monosodium_glutamate: DOMESTIC_SCHEMA_KEYS.monosodium_glutamate,
  added_dietary_fibre: DOMESTIC_SCHEMA_KEYS.added_dietary_fibre,
  mixture_of_sweeteners: DOMESTIC_SCHEMA_KEYS.mixture_of_sweeteners,
  mandatory_declaration: DOMESTIC_SCHEMA_KEYS.mandatory_declaration,
  common_salt_for: DOMESTIC_SCHEMA_KEYS.common_salt_for,
  wax: DOMESTIC_SCHEMA_KEYS.wax,
  name_of_oil: DOMESTIC_SCHEMA_KEYS.name_of_oil,
  refined_oil_name: DOMESTIC_SCHEMA_KEYS.refined_oil_name,
  blended_oil: DOMESTIC_SCHEMA_KEYS.blended_oil,
  percent_by_coffee: DOMESTIC_SCHEMA_KEYS.percent_by_coffee,
  percent_by_chicory: DOMESTIC_SCHEMA_KEYS.percent_by_chicory,
  package_material_type: DOMESTIC_SCHEMA_KEYS.package_material_type,
  brand_registration_number: DOMESTIC_SCHEMA_KEYS.brand_registration_number,
  recycling_icon: DOMESTIC_SCHEMA_KEYS.recycling_icon,
  pack_shape: DOMESTIC_SCHEMA_KEYS.pack_shape,
  unit: DOMESTIC_SCHEMA_KEYS.unit,
  pack_dimension: DOMESTIC_SCHEMA_KEYS.pack_dimension,
  label_dimension: DOMESTIC_SCHEMA_KEYS.label_dimension,
  sku_details: DOMESTIC_SCHEMA_KEYS.sku_details,
  mrp_title: DOMESTIC_SCHEMA_KEYS.mrp_title,
  mrp: DOMESTIC_SCHEMA_KEYS.mrp,
  usp_title: DOMESTIC_SCHEMA_KEYS.usp_title,
  date_of_pkg_mfg_title: DOMESTIC_SCHEMA_KEYS.date_of_pkg_mfg_title,
  typeOf_packaging_manufacture:
    DOMESTIC_SCHEMA_KEYS.typeOf_packaging_manufacture,
  dateOf_packaging_manufacture:
    DOMESTIC_SCHEMA_KEYS.dateOf_packaging_manufacture,
  useBy_expiry_title: DOMESTIC_SCHEMA_KEYS.useBy_expiry_title,
  expiry_durability_3_months: "expiry_durability_3_months",
  typeOf_useBy_expiry: DOMESTIC_SCHEMA_KEYS.typeOf_useBy_expiry,
  dateOf_useBy_expiry: DOMESTIC_SCHEMA_KEYS.dateOf_useBy_expiry,
  batch_number_title: DOMESTIC_SCHEMA_KEYS.batch_number_title,
  batch_no: DOMESTIC_SCHEMA_KEYS.batch_no,
  add_barcode: DOMESTIC_SCHEMA_KEYS.add_barcode,
  bar_code: DOMESTIC_SCHEMA_KEYS.bar_code,
  add_barcode_number: DOMESTIC_SCHEMA_KEYS.add_barcode_number,
  barcode_number: DOMESTIC_SCHEMA_KEYS.barcode_number,
  fssai_guidelines: DOMESTIC_SCHEMA_KEYS.fssai_guidelines,
  energy: DOMESTIC_SCHEMA_KEYS.energy,
  carbohydrates: DOMESTIC_SCHEMA_KEYS.carbohydrates,
  free_sugars: DOMESTIC_SCHEMA_KEYS.free_sugars,
  total_free_sugars: DOMESTIC_SCHEMA_KEYS.total_free_sugars,
  protein: DOMESTIC_SCHEMA_KEYS.protein,
  total_fat: DOMESTIC_SCHEMA_KEYS.total_fat,
  total_sfa: DOMESTIC_SCHEMA_KEYS.total_sfa,
  sodium: DOMESTIC_SCHEMA_KEYS.sodium,
  cholesterol: DOMESTIC_SCHEMA_KEYS.cholesterol,
  trans_fat: DOMESTIC_SCHEMA_KEYS.trans_fat,
  dietary_fiber: DOMESTIC_SCHEMA_KEYS.dietary_fiber,
  vitamin_d: DOMESTIC_SCHEMA_KEYS.vitamin_d,
  calcium: DOMESTIC_SCHEMA_KEYS.calcium,
  iron: DOMESTIC_SCHEMA_KEYS.iron,
  potassium: DOMESTIC_SCHEMA_KEYS.potassium,
  salt: DOMESTIC_SCHEMA_KEYS.salt,
  ins_number: DOMESTIC_SCHEMA_KEYS.ins_number,
  additive_value_show: DOMESTIC_SCHEMA_KEYS.additive_value_show,
  nature: DOMESTIC_SCHEMA_KEYS.nature,
  common_name: DOMESTIC_SCHEMA_KEYS.common_name,
  additive_name: DOMESTIC_SCHEMA_KEYS.additive_name,
  aspartame_methyl: DOMESTIC_SCHEMA_KEYS.aspartame_methyl,
  acesulfame_potassium: DOMESTIC_SCHEMA_KEYS.acesulfame_potassium,
  aspartame_salt: DOMESTIC_SCHEMA_KEYS.aspartame_salt,
  saccharins: DOMESTIC_SCHEMA_KEYS.saccharins,
  polyols: DOMESTIC_SCHEMA_KEYS.polyols,
  sorbitol: DOMESTIC_SCHEMA_KEYS.sorbitol,
  sucralose: DOMESTIC_SCHEMA_KEYS.sucralose,
  neotame: DOMESTIC_SCHEMA_KEYS.neotame,
  stevia_glycoside: DOMESTIC_SCHEMA_KEYS.stevia_glycoside,
  usp_rs: DOMESTIC_SCHEMA_KEYS.usp_rs,
  usp_unit: DOMESTIC_SCHEMA_KEYS.usp_unit,
  usp_auto_calculation: DOMESTIC_SCHEMA_KEYS.usp_auto_calculation,
  purity_weight_of_marker_compound:
    DOMESTIC_SCHEMA_KEYS.purity_weight_of_marker_compound,
  household_quantity: "household_quanity",
  translated_household_quantity: "translated_household_quantity",
  household_quantity_singapore: "household_quantity",
}

export interface Address {
  businessId: string
  addressId: string
  kindOfBusiness: string
  airlineName: string
  name: string
  addressLine1: string
  address_line_one: string
  addressLine2: string
  city: string
  state: string
  country: string
  postal_code: string
  postalCode: string | number
  coordinate: {
    latitude: number
    longitude: number
  }
  license: string
  id: string
  [key: string]: any
}

export interface ConsumerCareInfo {
  consumercare_addr_same_as_manufactured?: string
  address?: string
  email?: string
  phone?: string
  website?: string
}

export interface ConsumerCareData {
  address: string
  email: string
  phone: string
  website?: string
  consumer_care_dropdown_id: string
  [key: string]: any
}

export interface StorageInstructions {
  instructions_of_use?: string
  storage_instructions_text?: string
}

export interface Ingredient {
  compound: "Yes" | "No"
  ingredient_name: string
  name: string
  proportion: string
  show_ingredient_proportion: "Yes" | "No"
  is_sugar: boolean
  id: string
}

export type Unit = "kg" | "g" | "l" | "ml"
export type AgeGroup =
  | "0-6 months"
  | "7-12 months"
  | "1-3 years"
  | "4-6 years"
  | "7-9 years"
  | "10-12 years"
  | "13-15 years"
  | "16-18 years"
  | "Above 18 years"

export type NonMandatoryNutrient = { name: string; quantity: string }[]

export interface BaseLabelJSON {
  title: string
  other_information: string
  category: {
    category: string
    key: string
    description: string
    [key: string]: any
  }
  category_info: {
    level_1: string
    level_2: string
    [key: string]: any
  }
  created_on: string
  progress: number
  config: {
    package_shape: string
    label_pieces: string
    label_shape: string
    [key: string]: any
  }
  isLabelComplete: boolean
  mandatory_nutrient: any
  non_mandatory_nutrient: NonMandatoryNutrient

  currentStep: number
  country: string
  brandId: string
  brandName: string
  proprietary_product: string
  show_fssai_category: string
  PFL_RECIPE_SELECTION: {
    entry: {
      _id: string
      recipe_id: string
      recipe_code: string
      business_id: string
      brand_id: string[]
      name: string
      veg_non_vegeterian: string
      no_of_servings: number
      serving_size_unit: string
      recipe_yield: number
      serving_size: number
      recipe_yield_unit: string
      ni_info: {
        allergens: string[]
        [key: string]: any
      }
      comments: any[]
      type: string
      status: string
      modified_at: number
      createdAt: string
      updatedAt: string
      __v: number
      id: string
      [key: string]: any
    }
    [key: string]: any
  }
  ingredients: Ingredient[]
  finalingredients: {
    info: Ingredient
    name: string
    [key: string]: any
  }[]
  additives: {
    name: string
    uuid: string
    [key: string]: any
  }[]
  ingredient_message: boolean
  single_ingredients: string
  allergens: string
  contains: string[]
  may_contains: string[]
  ni_info_type: string
  serving_size: string
  serving_size_unit: Unit
  extraData: { selected_recipe?: PPFRecipe }
  flavour: string
  food_name: string
  actualProgress: number
  organic_product: string
  organic_certificate_from: any[]
  npop_accredited_certification_agency: string
  npop_accreditation_number: string
  pgs_india_organic_logo: string
  pgs_group_description: string
  pgs_unique_id_code: string
  agmark_certified: string
  tea_board_certified: string
  tea_india_certified: string
  bis_certified: string
  fortified: string
  irradiated: string
  natural_product: string
  plant_based_product: string
  vegetarian_and_non_vegetarian: string
  age_group: AgeGroup
  target_audience: string
  net_quantity: string
  net_quantity_unit: Unit
  manufacturer_by: string[]
  same_as_manufactured_by: any[]
  marketed_by: string[]
  consumer_care: ConsumerCareData[]
  use_storage_instructions: {
    storage_instructions_text: string
    [key: string]: any
  }[]
  plant_stanol_esters: string
  isomaltulose: string
  annetto_color: string
  "10%_or_more_polyols": string
  "10%_or_more_polydextrose": string
  added_caffeine: string
  "10%_or_more_sorbitol_and_sorbitol_syrup": string
  trehalose: string
  monosodium_glutamate: string
  added_dietary_fibre: string
  aspartame_methyl: string
  acesulfame_potassium: string
  aspartame_salt: string
  saccharins: string
  polyols: string
  sorbitol: string
  sucralose: string
  neotame: string
  stevia_glycoside: string
  mandatory_declaration: any[]
  package_material_type: string[]
  unit: string
  add_barcode: string
  add_barcode_number: string
  pack_shape: string
  pack_dimension: {
    height: string
    width: string
    [key: string]: any
  }[]
  label_dimension: {
    height: string
    width: string
    [key: string]: any
  }[]
  domestic_title: string
  copy_mandatory_nutrient: any[]
  brand_registration_number: string
  diplay_additives_from_domestic_label: string
  ingredientToDisplay: string
  translated_ingredients: string
  isModified: boolean
  additive_modified: boolean
  contains_to_display: string[] | string
  may_contains_to_display: string[] | string
  household_quanity: string
  translated_household_quantity: string
  imported_by: string
  country_of_origin: string
  batch_number_title: {
    batch_no: string
    [key: string]: any
  }[]
  barcode_number: string
  date_of_pkg_mfg_title: {
    dateOf_packaging_manufacture: string
    typeOf_packaging_manufacture: string
    [key: string]: any
  }[]
  useBy_expiry_title: {
    typeOf_useBy_expiry: string
    dateOf_useBy_expiry: string
    [key: string]: any
  }[]
  mrp: string
  aus_nz: any
  ni_info: Array<{
    name: string
    perServe?: string
    per100g?: string
    [key: string]: any
  }>
  [key: string]: any
}

export interface LabelJson {
  [key: string]: any
  vegetarian_and_non_vegetarian?: string
  brandId?: string
  food_name?: string
  consumer_care?: ConsumerCareInfo[]
  use_storage_instructions?: StorageInstructions[]
  country_of_origin?: string
  batch_number_title?: { batch_no?: string }[]
  date_of_pkg_mfg_title: {
    dateOf_packaging_manufacture: string
    typeOf_packaging_manufacture: string
    [key: string]: any
  }[]
  useBy_expiry_title: {
    typeOf_useBy_expiry: string
    dateOf_useBy_expiry: string
    [key: string]: any
  }[]
  mrp_title?: { mrp?: string }[]
  usp_title?: { usp_rs?: string; usp_unit?: string }[]
  add_barcode?: string
  bar_code?: string
  barcode_number?: string
  imported_by?: string
  expiry_durability_3_months: string
  flavour?: string
  other_information?: string
}

export interface RecipeNutritionalInfo {
  Energy: number
  Carbohydrates: number
  Free_Sugars: number
  Dietary_Fibre: number
  Insoluble_Fibre: number
  Soluble_Fibre: number
  Protein: number
  Total_Fat: number
  Total_SFA: number
  Trans_Fat: number
  Total_MUFA: number
  Total_PUFA: number
  Cholesterol: number
  Sodium: number
  Potassium: number
  Calcium: number
  Iron: number
  Zinc: number
  Phosphorus: number
  Magnesium: number
  Manganese: number
  Selenium: number
  Chromium: number
  Copper: number
  Aluminium: number
  Arsenic: number
  Cadmium: number
  Cobalt: number
  Lead: number
  Lithium: number
  Mercury: number
  Molybdenum: number
  Nickel: number
  Vit_A_Retinol: number
  Vit_A_Carotenoids: number
  Vit_A: number
  Vit_D: number
  Lutein: number
  Zeaxanthin: number
  Lycopene: number
  β_Cryptoxanthin: number
  γ_Carotene: number
  α_Carotene: number
  β_Carotene: number
  Vit_D3_Cholecalciferol: number
  Vit_D2_Ergocalciferol: number
  Vit_E_α_Tocopherol: number
  Phylloquinones: number
  Menaquinones: number
  Vit_B1_Thiamine: number
  Vit_B2_Riboflavin: number
  Vit_B3_Niacin: number
  Vit_B5_Pantothenic_Acid: number
  Vit_B6_Pyridoxine: number
  Biotin: number
  Vit_B9_Folate: number
  Vit_C_Ascorbic_Acid: number
  Total_Free_Sugars: number
  per_dv: {
    Energy: number
    Carbohydrates: number
    Free_Sugars: number
    Total_Free_Sugars: number
    Dietary_Fibre: number
    Protein: number
    Total_Fat: number
    Total_SFA: number
    Trans_Fat: number
    Cholesterol: number
    Sodium: number
    Vit_A: number
    Vit_C: number
    Vit_D: number
    Calcium: number
    Iron: number
    Potassium: number
  }
  healthTags: { displayLabel: string; key: string }[]
  allergens: string[]
  allergensMayContainList: string[]
  allergensGlobalList: string[]
  globalAllergensToDisplay: string
}

export interface PPFRecipeIngredient {
  ingredient_unit: string
  default_disabled: boolean
  ingredient_id: string
  ingredient_name: string
  ingredient_value: string
  ingredient_type: string
  ingredient_units: string[]
  ingredient_contain: string[]
  ingredient_may_contains: string[]
  veg_non_vegeterian: string
  ingredient_display_name: string
  otherNutrient?: { name: string; quantity: string }[]
}

export interface PPFRecipe {
  _id: string
  recipe_id: string
  recipe_code: string
  business_id: string
  brand_id: string
  name: string
  category: string
  veg_non_vegeterian: string
  no_of_servings: number
  serving_size_unit: string
  recipe_yield: number
  serving_size: number
  recipe_yield_unit: string
  ingredients: Ingredient[]
  ni_info: RecipeNutritionalInfo
  comments: string[]
  type: string
  status: string
  modified_at: number
  createdAt: string
  updatedAt: string
  __v: number
}

export const LESS_COMMONLY_REQUESTED_NUTRIENTS = {
  ORYZANOL: DOMESTIC_NUTRITION_KEYS.ORYZANOL,
  IRON: DOMESTIC_NUTRITION_KEYS.IRON,
  CALCIUM: DOMESTIC_NUTRITION_KEYS.CALCIUM,
  FOLIC_ACID: DOMESTIC_NUTRITION_KEYS.FOLIC_ACID,
  IODINE: DOMESTIC_NUTRITION_KEYS.IODINE,
  VITAMIN_B6: DOMESTIC_NUTRITION_KEYS.VITAMIN_B6,
  VITAMIN_B12: DOMESTIC_NUTRITION_KEYS.VITAMIN_B12,
  VITAMIN_C: DOMESTIC_NUTRITION_KEYS.VITAMIN_C,
  ZINC: DOMESTIC_NUTRITION_KEYS.ZINC,
  MAGNESIUM: DOMESTIC_NUTRITION_KEYS.MAGNESIUM, // Note spelling "mangesium" in DOMESTIC_NUTRITION_KEYS
  VITAMIN_D: DOMESTIC_NUTRITION_KEYS.VITAMIN_D,
  VITAMIN_K1: DOMESTIC_NUTRITION_KEYS.VITAMIN_K1,
  VITAMIN_B1: DOMESTIC_NUTRITION_KEYS.VITAMIN_B1,
  VITAMIN_B2: DOMESTIC_NUTRITION_KEYS.VITAMIN_B2,
  BIOTIN: DOMESTIC_NUTRITION_KEYS.BIOTIN,
  VITAMIN_B3: DOMESTIC_NUTRITION_KEYS.VITAMIN_B3, // Note spelling "vitami_b3" in DOMESTIC_NUTRITION_KEYS
  PANTOTHENIC_ACID: DOMESTIC_NUTRITION_KEYS.PANTOTHENIC_ACID, // "eicosa_pentadecanoic_acid" in DOMESTIC_NUTRITION_KEYS
  VITAMIN_E: DOMESTIC_NUTRITION_KEYS.VITAMIN_E,
  VITAMIN_A: DOMESTIC_NUTRITION_KEYS.VITAMIN_A,
  SELENIUM: DOMESTIC_NUTRITION_KEYS.SELENIUM,
  COPPER: DOMESTIC_NUTRITION_KEYS.COPPER,
  POTASSIUM: DOMESTIC_NUTRITION_KEYS.POTASSIUM,
  CHLORIDE: DOMESTIC_NUTRITION_KEYS.CHLORIDE,
  MANGANESE: DOMESTIC_NUTRITION_KEYS.MANGANESE,
  PHOSPHORUS: DOMESTIC_NUTRITION_KEYS.PHOSPHORUS,
} as const

export interface InterleavedOtherNutrient {
  name: string
  key: string
}

export interface NutrientDataForNiTable {
  key: string
  name: string
  per_serve: number | string
  per_100g?: number | string
  reni_value?: number | string
  unit?: string
}

export function extractNonMandatoryNutrient(
  interleaved_other_nutrients: InterleavedOtherNutrient[],
  non_mandatory_nutrient: NutrientDataForNiTable[],
): {
  interleaved_other_nutrients_data: Record<string, NutrientDataForNiTable>
  filtered_non_mandatory_nutrients: NutrientDataForNiTable[]
} {
  let interleaved_other_nutrients_data: Record<string, NutrientDataForNiTable> =
    {}

  // Filter out the nutrients that match our interleaved list, collecting them in interleaved_other_nutrients_data.
  const filtered_non_mandatory_nutrients =
    non_mandatory_nutrient?.filter((nutrient) => {
      const match_nutrient = interleaved_other_nutrients.find(
        (ele) => ele.key === nutrient.key,
      )
      if (match_nutrient) {
        interleaved_other_nutrients_data = {
          ...interleaved_other_nutrients_data,
          [match_nutrient.name]: nutrient,
        }

        return false
      } else {
        return true
      }
    }) || []

  const less_common_nutrient_order = Object.values(
    LESS_COMMONLY_REQUESTED_NUTRIENTS,
  ) as string[]

  // Sort the filtered nutrients according to their index in less_common_nutrient_order.
  filtered_non_mandatory_nutrients.sort((a, b) => {
    const idx_a = less_common_nutrient_order.indexOf(a.key)
    const idx_b = less_common_nutrient_order.indexOf(b.key)

    // If both keys exist in less_common_nutrient_order, compare their indices.
    if (idx_a !== -1 && idx_b !== -1) {
      return idx_a - idx_b
    }
    // If only one is present in the order array, that one comes first.
    if (idx_a !== -1 && idx_b === -1) return -1
    if (idx_a === -1 && idx_b !== -1) return 1

    // If neither is in the order array, they remain in their current relative order (stable sort).
    return 0
  })

  return {
    interleaved_other_nutrients_data,
    filtered_non_mandatory_nutrients,
  }
}

export function formatDateByCountry(dateString: string, country: string) {
  const [day, month, year] = dateString?.split("-")

  const date = new Date(`${year}-${month}-${day}`)

  const options: any = { day: "2-digit", month: "long", year: "numeric" }

  const localeDate = date?.toLocaleDateString("en-IN", options)

  const [formattedDay, formattedMonth, formattedYear] = localeDate.split(" ")

  const monthAbbreviations = [
    "JN",
    "FB",
    "MR",
    "AL",
    "MY",
    "JN",
    "JL",
    "AU",
    "SP",
    "OC",
    "NV",
    "DC",
  ]

  switch (country) {
    case COUNTRY_CODE.USA:
    case COUNTRY_CODE.UK:
      return `${month}-${day}-${year}`
    case COUNTRY_CODE["AUS-NZ"]:
      return `${day}/${month}/${year}`
    case COUNTRY_CODE.CANADA:
      const dateParts = dateString.split("-")
      const CA_year = dateParts[2].slice(2) // Last two digits of the year
      const CA_month = parseInt(dateParts[1], 10) // Convert month part to an integer
      const CA_day = dateParts[0]
      if (CA_month < 1 || CA_month > 12) {
        return "Invalid month"
      }
      return `${CA_day} ${monthAbbreviations[CA_month - 1]} ${CA_year}`
    case COUNTRY_CODE.GCC:
      return `${day}/${month}/${year}`
    case COUNTRY_CODE.PHILIPPINES: {
      const date = new Date(dateString)

      return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(date)
    }
    case COUNTRY_CODE.SINGAPORE:
    case COUNTRY_CODE.MALAYSIA: {
      const date = new Date(dateString)

      const dd = String(date.getDate()).padStart(2, "0")

      const mm = String(date.getMonth() + 1).padStart(2, "0")

      const yy = String(date.getFullYear()).slice(-2)
      return `${dd}/${mm}/${yy}`
    }

    // dd/mm/yy format
    case COUNTRY_CODE.HONG_KONG: {
      const date = new Date(dateString)

      const dd = String(date.getDate()).padStart(2, "0")

      const mm = String(date.getMonth() + 1).padStart(2, "0")

      const yy = String(date.getFullYear()).slice(-2)
      return `${dd}日 ${mm}月 ${yy}年`
    }

    default:
      return `${formattedDay} ${formattedMonth
        ?.toUpperCase()
        ?.slice(0, 3)} ${formattedYear}`
  }
}

export type CountryCode = (typeof COUNTRY_CODE)[keyof typeof COUNTRY_CODE]

export const showNiInfoOnOtherNutrients = (
  key: string,
  country: Partial<CountryCode>,
) => {
  if (country === COUNTRY_CODE.INDIA || !country) {
    return false
  }

  //below code always return false because currrently we put this feature on hold

  if (key === EXPORT_SCHEMA_KEYS.non_mandatory_nutrient) {
    const specific_countries: CountryCode[] = [
      COUNTRY_CODE.BHUTAN,
      COUNTRY_CODE.MALAYSIA,
    ]
    if (specific_countries.includes(country as CountryCode)) {
      // return true
      return false
    }
  }

  return false
}

export function getNiTableCalcualtedValueByCountry(
  country_code: CountryCode,
  label_json: any,
) {
  switch (country_code) {
    case COUNTRY_CODE.PHILIPPINES:
      return getPhilippinesNiTableCalculatedValue(label_json)

    case COUNTRY_CODE.MALAYSIA:
      return getMalaysiaNiTableCalculatedValue(label_json)

    case COUNTRY_CODE.SINGAPORE:
      return getSingaporeNiTableCalculatedValue(label_json)

    case COUNTRY_CODE.HONG_KONG:
      return getHongkongNiTableCalculatedValue(label_json)

    case COUNTRY_CODE.INDIA:
      return calculateIndiaNutritionTableValues(label_json)

    case COUNTRY_CODE.USA:
      return calculateUSANutritionTableValues(label_json)
    case COUNTRY_CODE.CANADA:
      return calculateCanadaNutritionTableValues(label_json)
    case COUNTRY_CODE.GCC:
      return calculateGCCNutritionTableValues(label_json)

    default:
      return null
  }
}

export function addMultiCountryNiTableCalculatedValue(label_json: any) {
  if (!label_json || typeof label_json !== "object") return

  // label_json = multi country label json
  for (const country_code of Object.keys(label_json)) {
    if (
      ([COUNTRY_CODE.INDIA, COUNTRY_CODE.USA] as CountryCode[]).includes(
        country_code as CountryCode,
      )
    ) {
      const country_label = label_json[country_code]
      //country specific label

      if (!country_label?.label_json) continue

      const ni_table_calculated_values = getNiTableCalcualtedValueByCountry(
        country_code as CountryCode,
        country_label?.label_json,
      )

      if (label_json[country_code] && label_json[country_code].label_json) {
        label_json[country_code].label_json.ni_table_calculated_values =
          ni_table_calculated_values
      }
    }
  }

  return label_json
}

export function renderInstructionsOfUse(full_text: string) {
  if (!full_text?.trim()) return null

  const trimmed_text = full_text.trim()

  const numbered_instructions = trimmed_text.match(
    /\d{1,2}\.\s[\s\S]*?(?=(\d{1,2}\.\s)|$)/g,
  )

  const intro_text = trimmed_text.split(/\d{1,2}\.\s/)[0].trim()

  return (
    <>
      {intro_text && !/^\d{1,2}\.\s/.test(intro_text) && (
        <p className="text-[15px] leading-tight">{intro_text}</p>
      )}

      {numbered_instructions?.map((instruction, index) => (
        <p key={index} className="text-[15px] leading-tight">
          {instruction.trim()}
        </p>
      ))}
    </>
  )
}

/**
 * Rounds a number to the specified number of significant figures.
 *
 * @param nutrient_value      The numeric value to round.
 * @param significant_digits  The number of significant figures to round to.
 * @returns                   The value rounded to the given sig figs.
 *
 * Examples:
 *   (55.749, 3)  → 55.7
 *   (469.05, 3)  → 469
 *   (1355.4, 3)  → 1360
 *   (55.749, 2)  → 56
 *   (469.05, 2)  → 470
 *   (1355.4, 2)  → 1400
 */
export function roundOffToSignificantFigures(
  nutrient_value: number,
  significant_digits: number,
): number {
  // Handle non-finite values and zero
  if (!isFinite(nutrient_value) || nutrient_value === 0) {
    return nutrient_value
  }

  const abs_value = Math.abs(nutrient_value)
  // Determine magnitude (10^m) of the value
  const magnitude = Math.floor(Math.log10(abs_value))
  // Compute factor to shift decimal so that we keep `significant_digits` digits
  // e.g. magnitude=1, sig=3  →  factor = 10^(1 - 3 + 1) = 0.1
  //      magnitude=3, sig=2  →  factor = 10^(3 - 2 + 1) = 10
  const factor = Math.pow(10, magnitude - significant_digits + 1)

  // Round the shifted value, then shift back
  const rounded_abs = Math.round(abs_value / factor) * factor

  // figure out how many decimals we need in the final string
  const decimal_places = Math.max(significant_digits - magnitude - 1, 0)

  // toFixed here removes the tiny floating-point residue
  const rounded_fixed = rounded_abs.toFixed(decimal_places)
  const result = Number(rounded_fixed)

  return nutrient_value < 0 ? -result : result
}
